/**
 * One news article page's content: picks the current language's article, writes its meta tags
 * and `NewsArticle` JSON-LD, and renders `./NewsArticle.tsx`.
 *
 * Rendered by `src/pages/about-us/news-events/news/[categorySlug]/[titleSlug].tsx` with the
 * build-time props from `src/contentful/news/getContentfulNewsArticleDetails.ts` and
 * `getContentfulNewsForSections.ts`.
 *
 * The meta tags, as the code stands:
 * - `title` and `description` are the article's `seoTitle` and `seoDescription`, and are empty
 *   strings when Contentful has none; nothing falls back to `title` or `subTitle`.
 * - The path's category segment is rebuilt here from the `category` label (lower case, spaces to
 *   hyphens). For the four known labels this equals the slug from
 *   `src/contentful/news/helpers/index.ts` → `getNewsCategorySlugFromCategoryLabel`; the two
 *   must stay in step.
 * - `company: false`, so no Organization JSON-LD. The `NewsArticle` JSON-LD is filled in English
 *   only (French and Spanish emit `{}`); its `@id` and logo URL are site-relative and `image` is
 *   empty.
 */
import React from "react";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import { Language, useApplication } from "@ryerson/frontend.application";
import NewsArticle from "./NewsArticle";
import MetaHead, { MetaContent } from "@components/Shared/model/MetaHead";
import {
	ProcessedContentfulNewsArticleAllFieldsByLanguage,
	ProcessedContentfulNewsForSectionsByLanguage,
} from "@contentful/news/types";

/** The article page's own props, passed through unchanged. */
interface NewsArticleContainerProps {
	contentfulNewsArticleByLanguage: ProcessedContentfulNewsArticleAllFieldsByLanguage;
	contentfulNewsByLanguage: ProcessedContentfulNewsForSectionsByLanguage;
}

/**
 * Writes the article's meta tags and JSON-LD and renders the article body in the current language.
 */
const NewsArticleContainer: React.FC<NewsArticleContainerProps> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	// const data = props.pageResources.json.pageContext.contentfulData;
	const contentfulNewsArticleForLanguage = props.contentfulNewsArticleByLanguage[language];

	let metaObj: LanguageContent<MetaContent> = {
		en: {
			title: "",
			description: "",
			url: "",
			company: false,
		},
		fr: {
			title: "",
			description: "",
			url: "",
			company: false,
		},
		es: {
			title: "",
			description: "",
			url: "",
			company: false,
		},
	};
	let structuredContent: any = {};
	let metaObjForLanguage = {
		title: contentfulNewsArticleForLanguage.seoTitle ?? "",
		description: contentfulNewsArticleForLanguage.seoDescription ?? "",
		url:
			"/about-us/news-events/news/" +
			contentfulNewsArticleForLanguage.category.toLowerCase().replaceAll(" ", "-") +
			"/" +
			contentfulNewsArticleForLanguage.slug,
		company: false,
	};

	// Only the current language's meta entry is filled; it is the only one read below.
	if (language === Language.en) {
		metaObj.en = metaObjForLanguage;
		structuredContent = {
			"@context": "https://schema.org",
			"@type": "NewsArticle",
			mainEntityOfPage: {
				"@type": "WebPage",
				"@id":
					"/about-us/news-events/news/" +
					contentfulNewsArticleForLanguage.category.toLowerCase().replaceAll(" ", "-") +
					"/" +
					contentfulNewsArticleForLanguage.slug,
			},
			headline: contentfulNewsArticleForLanguage.title,
			description: contentfulNewsArticleForLanguage.seoDescription,
			image: "",
			author: {
				"@type": "Organization",
				name: "Ryerson",
			},
			publisher: {
				"@type": "Organization",
				name: "Ryerson",
				logo: {
					"@type": "ImageObject",
					url: "/images/RyersonLogo.png",
				},
			},
			datePublished: contentfulNewsArticleForLanguage.publishDate,
		};
	} else if (language === Language.fr) {
		metaObj.fr = metaObjForLanguage;
	} else {
		metaObj.es = metaObjForLanguage;
	}

	return (
		<>
			{/* NOTE: the metaObj[language] will not work for SEO for following reasons:
					1. HasMounted blocks server render, so HTML (including meta tags) is empty → bad for SEO
					2. Localization comes from useState + useContext, which only works after hydration
					3. metaObj tags set via metaObj[language] rely on client-only state, so not seen by crawlers
					
					For SSG and SEO, we will need:
					1. Remove HasMounted and address the underlying issue
					2. Determine how to handle localization when deciding what crawlers should see in the meta tags
					3. Pass localization as a prop, not via useContext
				*/}
			<MetaHead content={metaObj[language]}>
				<script type="application/ld+json">{JSON.stringify(structuredContent)}</script>
			</MetaHead>
			<NewsArticle
				contentfulNewsByLanguage={props.contentfulNewsByLanguage}
				contentfulNewsArticleForLanguage={contentfulNewsArticleForLanguage}
			/>
		</>
	);
};

export default NewsArticleContainer;
